<template>
  <base-drawer
    class="drawer-form add-booking-drawer"
    :show="show"
    title="Add booking"
    width="520px"
    @close="onClose"
  >
    <drawer-section title="Event and time" icon="icon-calendar-60">
      <base-input label="Event" :error="errs.game">
        <el-select
          v-model="selectedGameId"
          class="select-primary"
          placeholder="Select an event"
          value-key="id"
          @change="onGameChange"
        >
          <el-option
            v-for="game in games"
            :key="'G' + game.id"
            class="select-primary"
            :label="game.name"
            :value="game.id"
          />
        </el-select>
      </base-input>

      <div v-if="selectedGameId" class="row">
        <div class="col-6">
          <base-input label="Booking date" :error="errs.booking_date">
            <el-date-picker
              v-model="bookingDate"
              type="date"
              placeholder="Select date"
              :format="pickerFormat"
              :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
              @change="onDateChange"
            />
          </base-input>
        </div>
        <div class="col-6">
          <base-input label="Time slot" :error="errs.time_slot">
            <el-select
              v-model="selectedSlotId"
              class="select-primary"
              placeholder="Time"
              :disabled="!bookingDate || loadingSlots"
              :loading="loadingSlots"
              @change="onSlotChange"
            >
              <el-option
                v-for="slot in availableSlots"
                :key="'S' + slot.id"
                class="select-primary"
                :label="slotLabel(slot)"
                :value="slot.id"
              >
                <span>{{ slotLabel(slot) }}</span>
                <span v-if="slot.status === 'blocked' && !slot.status_type_id" class="badge badge-compact badge-danger ml-2">Blocked</span>
                <span v-if="slot.status === 'call_to_book'" class="badge badge-compact badge-warning ml-2">Call to book</span>
                <span v-if="slot.status === 'requestable'" class="badge badge-compact ml-2" :style="{ backgroundColor: '#FFD600', color: '#1E1E2F' }">Requestable</span>
                <span v-if="slot.status === 'requested'" class="badge badge-compact badge-info ml-2">Requested</span>
                <span v-if="slot.status_type_id" class="badge badge-compact ml-2" :style="{ backgroundColor: slot.status_color, color: slot.status_text_color }">{{ slot.status_display_name }}</span>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>
    </drawer-section>

    <drawer-section v-if="selectedSlot" title="Tickets" icon="icon-single-02">
      <div v-if="durationOptions.length > 1" class="mb-3">
        <label class="text-white d-block mb-1">Duration</label>
        <segmented-toggle
          variant="pills"
          size="sm"
          :value="selectedDurationId"
          :options="durationOptions"
          :disabled="anyRowEditing"
          @input="onDurationChange"
        />
      </div>

      <info-hint v-if="categories.length === 0" variant="warning" icon="icon-alert-circle-exc">
        This time slot has no pricing categories for the selected duration.
      </info-hint>

      <div v-for="c in categories" :key="'C' + c.category_id" class="pricing-row">
        <div class="pricing-row__info">
          <div class="pricing-row__name-line">
            <span class="pricing-row__name text-white">{{ c.category }}</span>
            <el-tooltip v-if="!c.editing" content="Override price" placement="top" :open-delay="300">
              <i class="tim-icons icon-pencil pricing-row__edit-icon" @click="startEditPrice(c)"></i>
            </el-tooltip>
          </div>
          <span v-if="!c.editing" class="pricing-row__rate">{{ getRate(c) }}</span>
          <div v-else class="pricing-row__inline-edit">
            <base-input class="mb-0 pricing-row__price-input">
              <el-input-number
                v-model="c.price"
                :min="0"
                :precision="2"
                :step="0.01"
                :controls="false"
                size="mini"
              />
            </base-input>
            <el-tooltip content="Save" placement="top" :open-delay="300">
              <i class="tim-icons icon-check-2 pricing-row__save-icon" @click="saveEditPrice(c)"></i>
            </el-tooltip>
            <el-tooltip content="Restore original price" placement="top" :open-delay="300">
              <i class="tim-icons icon-refresh-02 pricing-row__restore-icon" @click="restoreOriginalPrice(c)"></i>
            </el-tooltip>
            <el-tooltip content="Cancel" placement="top" :open-delay="300">
              <i class="tim-icons icon-simple-remove pricing-row__cancel-icon" @click="cancelEditPrice(c)"></i>
            </el-tooltip>
          </div>
        </div>
        <div class="pricing-row__qty">
          <base-input class="mb-0">
            <el-input-number v-model="c.number_of_players" :min="0" size="mini" />
          </base-input>
        </div>
        <div class="pricing-row__total text-white">{{ getAmount(getLineTotal(c)) }}</div>
      </div>

      <div v-if="categories.length > 0" class="summary-line summary-line--total">
        <span class="text-white">Subtotal</span>
        <span class="text-white">{{ getAmount(subtotal) }}</span>
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" :disabled="saving" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving || anyRowEditing" :loading="saving" @click="onSave">Add booking</base-button>
      </div>
    </template>

    <base-alert-dialog
      :show="emailPrompt.show"
      title="Send confirmation email?"
      message="An email automation is configured for new bookings. Do you want to send the confirmation email now?"
      variant="info"
      show-cancel
      confirm-text="Yes, send now"
      cancel-text="No, send later"
      :dismissible="false"
      @confirm="onEmailPromptAnswer(true)"
      @cancel="onEmailPromptAnswer(false)"
    />
  </base-drawer>
</template>

<script setup lang="ts">
// Add a booking to an existing transaction. Owns its own event, date, slot and
// ticket state; the page only opens it and refreshes on `added`. Pricing math
// is the shared calculateSubtotal (the same code the booking drawer uses) and
// the price override follows CreateBookingView's start/save/cancel/restore.
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import _ from 'lodash'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { time } from '@/plugins/dateFormatter'
import { pickerDateFormat } from '@/plugins/dates'
import { getGroupSettings, formatCurrency } from '@/util/currencyFormat'
import {
  calculateSubtotal,
  findPriceForPlayerCount,
  roundCents,
  slotDurationSettings,
  durationPickerOptions,
  slotPricingRows
} from '@/util/pricing'

interface Category {
  category_id: number
  category: string
  number_of_players: number
  price: number
  original_price?: number
  category_pricing: any[]
  editing?: boolean
  _preEditPrice?: number
  _preEditOverride?: number
}

const props = defineProps({
  show: { type: Boolean, default: false },
  transactionId: { type: [Number, String], default: null },
  hasBookingCreatedAutomation: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'added'])

const { proxy } = getCurrentInstance() as any

const group = getGroupSettings()
const companyId = localStorage.getItem('companyId')
const companyGroupId = group.id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const pickerFormat = (group.show_day_of_week ? 'ddd, ' : '') + pickerDateFormat(group.date_format)
const firstDayOfWeek = group.first_day_of_week || 7

const games = ref<any[]>([])
const selectedGameId = ref<number | null>(null)
const bookingDate = ref<Date | null>(null)
const availableSlots = ref<any[]>([])
const selectedSlotId = ref<number | null>(null)
const selectedDurationId = ref<number | null>(null)
const categories = ref<Category[]>([])
const loadingSlots = ref(false)
const saving = ref(false)
const errs = ref({ game: '', booking_date: '', time_slot: '' })
const emailPrompt = ref({ show: false })

const selectedSlot = computed(() => availableSlots.value.find(s => s.id === selectedSlotId.value) || null)

const isCustom = computed(() => !!selectedSlot.value && Number(selectedSlot.value.is_custom_pricing_enabled) === 1)
const pricingTypeId = computed(() => {
  const s = selectedSlot.value || {}
  return Number(isCustom.value ? s.slot_pricing_type_id : s.pricing_type_id) || 1
})
const cumulativePricing = computed(() => {
  const s = selectedSlot.value || {}
  return Number(isCustom.value ? s.slot_cumulative_category_pricing : s.cumulative_category_pricing) === 1
})
const pricingRows = computed(() => (selectedSlot.value ? slotPricingRows(selectedSlot.value, selectedDurationId.value) : []))
const durationOptions = computed(() => {
  const settings = slotDurationSettings(selectedSlot.value || {})
  return settings.enabled ? durationPickerOptions(settings.durations) : []
})
const totalPlayers = computed(() => categories.value.reduce((sum, c) => sum + (Number(c.number_of_players) || 0), 0))
const subtotal = computed(() => {
  if (!selectedSlot.value) return 0
  const result = calculateSubtotal({
    pricing: pricingRows.value,
    categories: categories.value,
    cumulativePricing: cumulativePricing.value,
    pricingTypeId: pricingTypeId.value
  })
  return roundCents(result.subtotal)
})
const anyRowEditing = computed(() => categories.value.some(c => c.editing))

function getAmount(value: number) {
  return formatCurrency(value, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

function slotLabel(slot: any) {
  return time(group.time_format, slot.start_time) + ' - ' + time(group.time_format, slot.end_time)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function notifyError(err: any, fallback: string) {
  if (err && err.response && err.response.status === 401) {
    proxy.$router.push('/forbidden')
    return
  }
  let message = fallback
  const data = err && err.response ? err.response.data : null
  if (typeof data === 'string' && data) {
    try { message = JSON.parse(data).error || data } catch (e) { message = data }
  } else if (data && typeof data === 'object' && data.error) {
    message = data.error
  }
  proxy.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc', timeout: 5000 })
}

async function fetchGames() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/games`, config)
    games.value = Array.isArray(response.data) ? response.data : []
  } catch (err) {
    console.error('Failed to load events:', err)
    notifyError(err, 'Could not load events')
  }
}

function toIsoDate(d: Date) {
  return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
}

async function fetchAvailableSlots() {
  availableSlots.value = []
  selectedSlotId.value = null
  categories.value = []
  if (!selectedGameId.value || !bookingDate.value) return
  loadingSlots.value = true
  try {
    const config = await authConfig()
    const url = `${baseUrl}/bookings/available?t=${toIsoDate(bookingDate.value)}&game_id=${selectedGameId.value}&include_blocked=1`
    const response = await axios.get(url, config)
    availableSlots.value = (response.data.bookings || []).map((slot: any) => ({
      ...slot,
      pricing: parseJson(slot.pricing),
      custom_pricing: parseJson(slot.custom_pricing),
      all_pricing: parseJson(slot.all_pricing),
      slot_durations: parseJson(slot.slot_durations),
      game_durations: parseJson(slot.game_durations)
    }))
  } catch (err) {
    console.error('Failed to load time slots:', err)
    notifyError(err, 'Could not load time slots')
  } finally {
    loadingSlots.value = false
  }
}

function parseJson(value: any) {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || !value) return []
  try { return JSON.parse(value) } catch (e) { return [] }
}

// One row per category, seeded with the category's default participant count
function seedCategories() {
  const rows = pricingRows.value
  const seeded: Category[] = []
  for (const row of rows) {
    if (seeded.find(c => c.category_id === row.category_id)) continue
    seeded.push({
      category_id: row.category_id,
      category: String(row.category || '').toLowerCase(),
      number_of_players: Number(row.default_participants) || 0,
      price: Number(row.price) || 0,
      category_pricing: rows.filter(r => r.category_id === row.category_id)
    })
  }
  seeded.sort((a, b) => a.category_id - b.category_id)
  categories.value = seeded
}

function onGameChange() {
  errs.value.game = ''
  bookingDate.value = null
  availableSlots.value = []
  selectedSlotId.value = null
  categories.value = []
}

function onDateChange() {
  errs.value.booking_date = ''
  fetchAvailableSlots()
}

function onSlotChange() {
  errs.value.time_slot = ''
  selectedDurationId.value = null
  seedCategories()
}

function onDurationChange(value: number | null) {
  selectedDurationId.value = value
  seedCategories()
}

function getUnitPrice(c: Category) {
  if (Object.prototype.hasOwnProperty.call(c, 'original_price')) return Number(c.price) || 0
  const count = cumulativePricing.value ? totalPlayers.value : Number(c.number_of_players) || 0
  const tierPrice = findPriceForPlayerCount(c.category_pricing, count)
  return tierPrice || Number(c.price) || 0
}

function getRate(c: Category) {
  const unit = getAmount(getUnitPrice(c))
  return pricingTypeId.value === 1 ? `${unit} per ${c.category}` : `${unit} flat rate for ${c.category}`
}

function getLineTotal(c: Category) {
  const qty = Number(c.number_of_players) || 0
  if (qty <= 0) return 0
  const unit = getUnitPrice(c)
  return roundCents(pricingTypeId.value === 1 ? unit * qty : unit)
}

// Price override: snapshot on start so cancel returns to the exact prior state
function startEditPrice(c: Category) {
  proxy.$set(c, '_preEditPrice', c.price)
  proxy.$set(c, '_preEditOverride', c.original_price)
  if (c.original_price === undefined) {
    proxy.$set(c, 'original_price', getUnitPrice(c))
    c.price = getUnitPrice(c)
  }
  proxy.$set(c, 'editing', true)
}

function saveEditPrice(c: Category) {
  proxy.$delete(c, '_preEditPrice')
  proxy.$delete(c, '_preEditOverride')
  proxy.$set(c, 'editing', false)
}

function cancelEditPrice(c: Category) {
  if (c._preEditPrice !== undefined) c.price = c._preEditPrice
  if (c._preEditOverride !== undefined) {
    proxy.$set(c, 'original_price', c._preEditOverride)
  } else {
    proxy.$delete(c, 'original_price')
  }
  proxy.$delete(c, '_preEditPrice')
  proxy.$delete(c, '_preEditOverride')
  proxy.$set(c, 'editing', false)
}

function restoreOriginalPrice(c: Category) {
  if (c.original_price !== undefined) {
    c.price = c.original_price
    proxy.$delete(c, 'original_price')
  }
  proxy.$delete(c, '_preEditPrice')
  proxy.$delete(c, '_preEditOverride')
  proxy.$set(c, 'editing', false)
}

function validate() {
  let valid = true
  errs.value.game = selectedGameId.value ? '' : 'Select an event'
  errs.value.booking_date = bookingDate.value ? '' : 'Booking date cannot be empty'
  errs.value.time_slot = selectedSlotId.value ? '' : 'Time slot cannot be empty'
  if (errs.value.game || errs.value.booking_date || errs.value.time_slot) valid = false
  return valid
}

function onSave() {
  if (!validate()) return
  if (props.hasBookingCreatedAutomation) {
    emailPrompt.value.show = true
    return
  }
  submit(true)
}

function onEmailPromptAnswer(sendNow: boolean) {
  emailPrompt.value.show = false
  submit(sendNow)
}

async function submit(sendConfirmationEmail: boolean) {
  const slot = selectedSlot.value
  if (!slot) return
  saving.value = true
  try {
    const config = await authConfig()
    const data = {
      booking_slot_id: slot.id,
      number_of_players: totalPlayers.value,
      price: subtotal.value,
      tickets: categories.value.map(c => ({
        category_id: c.category_id,
        category: c.category,
        number_of_players: Number(c.number_of_players) || 0,
        price: c.price,
        ...(c.original_price !== undefined ? { original_price: c.original_price } : {}),
        category_pricing: c.category_pricing
      })),
      selected_duration_id: selectedDurationId.value,
      send_confirmation_email: sendConfirmationEmail
    }
    await axios.put(`${baseUrl}/transactions/${props.transactionId}/bookings`, data, config)
    proxy.$notify({ type: 'success', message: 'Booking has been added to the transaction', icon: 'tim-icons icon-check-2' })
    emit('added')
    emit('close')
  } catch (err) {
    console.error('Failed to add booking:', err)
    notifyError(err, 'Something went wrong')
  } finally {
    saving.value = false
  }
}

function resetForm() {
  selectedGameId.value = null
  bookingDate.value = null
  availableSlots.value = []
  selectedSlotId.value = null
  selectedDurationId.value = null
  categories.value = []
  errs.value = { game: '', booking_date: '', time_slot: '' }
  emailPrompt.value.show = false
}

function onClose() {
  if (saving.value) return
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show && !emailPrompt.value.show) onClose()
}

watch(() => props.show, (open) => {
  if (open) {
    resetForm()
    document.addEventListener('keydown', onEscKey)
    fetchGames()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
export default { name: 'AddBookingDrawer' }
</script>
